<template>
  <div 
    ref="container" 
    class="formula-block markdown-content"
    v-html="html"
  ></div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick } from 'vue'
import { ensureMathJax } from '@/main.js'

const props = defineProps({
  html: { 
    type: String, 
    default: '' 
  }
})

const container = ref(null)
let mathJaxLoaded = false

async function typeset() {
  await nextTick()
  
  // 按需加载 MathJax（只在需要渲染公式时加载）
  if (!mathJaxLoaded) {
    try {
      await ensureMathJax()
      mathJaxLoaded = true
    } catch (error) {
      console.warn('Failed to load MathJax:', error)
      return
    }
  }
  
  const mj = window.MathJax
  
  if (mj && container.value) {
    try {
      // 清理可能存在的错误信息
      const errorElements = container.value.querySelectorAll('.MathJax_Error, .mjx-error')
      errorElements.forEach(el => el.remove())
      
      // 使用 MathJax 3.x 的 typesetPromise API
      await mj.typesetPromise([container.value])
    } catch (error) {
      console.warn('MathJax typeset error:', error)
      // 隐藏 MathJax 错误信息
      const errorElements = container.value?.querySelectorAll('.MathJax_Error, .mjx-error')
      errorElements?.forEach(el => {
        el.style.display = 'none'
      })
    }
  }
}

onMounted(() => {
  // 只在有内容时才加载 MathJax
  if (props.html) {
    typeset()
  }
})

watch(() => props.html, () => {
  if (props.html) {
    typeset()
  }
})
</script>

<style scoped>
.formula-block {
  line-height: 1.8;
}

.formula-block :deep(.math-block) {
  margin: 1.5em 0;
  text-align: center;
  overflow-x: auto;
  padding: 0.5em;
}

.formula-block :deep(.math-inline) {
  display: inline;
}

.formula-block :deep(.mjx-chtml) {
  font-size: 1em;
}

/* 表格样式 */
.formula-block :deep(table) {
  border-collapse: collapse;
  margin: 1.5em 0;
  width: 100%;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.formula-block :deep(table th),
.formula-block :deep(table td) {
  border: 1px solid #e0e0e0;
  padding: 0.75em 1em;
  text-align: left;
}

.formula-block :deep(table th) {
  background-color: #f5f5f5;
  font-weight: 600;
  color: #333;
}

.formula-block :deep(table tr:nth-child(even)) {
  background-color: #fafafa;
}

.formula-block :deep(table tr:hover) {
  background-color: #f0f0f0;
}

/* 文本格式 */
.formula-block :deep(strong) {
  font-weight: 600;
  color: #333;
}

.formula-block :deep(em) {
  font-style: italic;
}

/* 响应式表格 */
@media (max-width: 768px) {
  .formula-block :deep(table) {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
}
</style>